import { Alert, Button, Icon, Modal } from '@distribusion/owl-frontend-ui'
import { Field, useFormikContext } from 'formik'
import React, { ReactElement, useEffect } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'

import bem from '@lib/bem'
import { useTranslation } from '@lib/i18n'
import { password, required, chain, exact } from '@lib/validators'
import { useChangePassword } from '@loaders/changePassword'
import { AccountFormData } from '@pages/Account/Form/Provider'
import { ModalType, MODAL_TYPES } from '@pages/Account/hooks/usePasswordChange'
import PasswordField from '@ui/Input/PasswordField'

import '@pages/Account/Settings/Modals/ResetPasswordModal/index.scss'

interface ResetPasswordModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  onError: (errorType: ModalType) => void
}

export const ResetPasswordModal = ({ isOpen, onClose, onSuccess, onError }: ResetPasswordModalProps): ReactElement => {
  const { t } = useTranslation()
  const { values, errors, touched, setFieldValue } = useFormikContext<AccountFormData>()
  const { userId, retailerPartnerNumber } = useParams()
  const [searchParams] = useSearchParams()

  const { mutateAsync: changePassword } = useChangePassword()

  const verificationCode = searchParams.get('verification_code')
  const hasVerificationCode = !!verificationCode
  const newPassword = values.settings.newPassword?.trim() || ''
  const confirmPassword = values.settings.confirmPassword?.trim() || ''
  const hasNewPasswordError = touched.settings?.newPassword && !!errors.settings?.newPassword
  const hasConfirmPasswordError = touched.settings?.confirmPassword && !!errors.settings?.confirmPassword
  const passwordsMatch = newPassword === confirmPassword

  const isButtonDisabled =
    !hasVerificationCode ||
    !newPassword ||
    !confirmPassword ||
    !passwordsMatch ||
    hasNewPasswordError ||
    hasConfirmPasswordError

  useEffect(() => {
    if (!isOpen) {
      setFieldValue('settings.newPassword', '')
      setFieldValue('settings.confirmPassword', '')
    }
  }, [isOpen, setFieldValue])

  const handleSubmit = async (): Promise<void> => {
    if (!userId || !hasVerificationCode) /* istanbul ignore next */ return

    try {
      await changePassword({
        userId,
        retailerPartnerNumber: Number(retailerPartnerNumber),
        newPassword,
        verificationCode,
      })
      onSuccess()
    } catch (error) {
      onError(MODAL_TYPES.RESET_PASSWORD)
    }
  }

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title={t('account.passwordRecovery.resetModal.title')}
      maxWidth="sm"
      dataTag="reset-password-modal"
    >
      <div className={bem('reset-password-modal', 'content')}>
        <span>{t('account.passwordRecovery.resetModal.description')}</span>
        <Field
          component={PasswordField}
          label={t('account.passwordRecovery.resetModal.newPassword')}
          name="settings.newPassword"
          type="password"
          validate={chain([required, password])}
          required
          dataTag="reset-new-password-input"
        />
        <div className={bem('reset-password-modal', 'password-info')}>
          <Icon name="info" size="medium" className={bem('password-info', 'icon')} />
          <p>{t('checkout.createAccount.hint')}</p>
        </div>
        <Field
          component={PasswordField}
          label={t('account.passwordRecovery.resetModal.confirmPassword')}
          name="settings.confirmPassword"
          type="password"
          validate={chain([
            required,
            value => exact(values.settings.newPassword ?? '', t('errors.invalidPasswordMatch'))(value),
          ])}
          required
          dataTag="reset-confirm-password-input"
        />
        <Alert message={t('account.passwordRecovery.resetModal.logoutHint')} type="error" variant="compact" />
        <div className={bem('reset-password-modal', 'actions')}>
          <Button color="primary" variant="outline" onClick={onClose}>
            {t('account.passwordRecovery.resetModal.cancelButton')}
          </Button>
          <Button color="primary" onClick={handleSubmit} disabled={isButtonDisabled} dataTag="submit-reset-password">
            {t('account.passwordRecovery.resetModal.resetButton')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
